/** Dispatches cursor composer text and captured context through the shared submit path. */
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import {
  beginCompactPromptScreenshotPreview,
  finishCompactResources,
} from "@/desktop/compact-resource-controller";
import {
  CURSOR_INPUT_SCREENSHOT_CAPTURE_SCOPE,
  shouldCaptureCursorInputScreenshot,
} from "@/desktop/cursor-input-screenshot";
import { requestsForegroundScreenContext } from "@/desktop/foreground-screen-intent";
import { holdSendForGuide } from "@/desktop/onboarding/quick-guide/guide-send-hold";
import { attachmentIsImage } from "@/lib/composer-attachment";
import { modelSupportsVision } from "@/lib/model-vision";
import { translate } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";
import { useNotificationStore } from "@/stores/notification-store";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { imageFrameSize, imagePixelSize } from "@/lib/image-frame-size";
import { destinationDraftComposerId } from "@/hooks/use-destination-draft";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useCanvasStore } from "@/stores/canvas-store";
import { inputDestinationKey } from "@/lib/input-destination-key";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useCallback } from "react";
import {
  clearAttemptScreenshots,
  shouldCompleteInputSubmit,
} from "./input-submit-outcome";
import type { useMasterLayoutMedia } from "./use-master-layout-media";
import type { useMasterLayoutShell } from "./use-master-layout-shell";
import { ingestFrame } from "@/lib/frame-ingest";

export function useMasterCursorInputSubmit({
  shell,
  media,
  resolveModelForCapture,
  summonSelectionPresent,
}: {
  shell: ReturnType<typeof useMasterLayoutShell>;
  media: ReturnType<typeof useMasterLayoutMedia>;
  resolveModelForCapture: () => string | undefined;
  summonSelectionPresent: boolean;
}): CursorInputContextValue["onSubmit"] {
  const {
    dispatchUserMessage,
    data,
    maxParallelAgents,
    pushActivity,
    attachedImages,
  } = shell;
  const {
    captureAndAttachIfEnabled,
    handleRemoveImage,
    screenshotIntent,
    copyImagesToCanvas,
    setScreenshotIntent,
  } = media;
  // Share uploaded files and captured context in one acknowledged dispatch.
  const handleCursorInputSubmit = useCallback(
    async (
      rawText: string,
      explicitAgentKey?: string,
      options?: Parameters<CursorInputContextValue["onSubmit"]>[2],
    ) => {
      if (holdSendForGuide("typed")) return false;
      // A send spans awaits; it places frames in the view it started in.
      const view = workspaceViewAdapter();
      const text = rawText.trim();
      const inputBarRoutingIntent = options?.ignoreInputBarSessionState
        ? undefined
        : useInputBarTarget.getState().snapshotRoutingIntent();
      const submittedImages =
        options?.textOnly && !options?.allowImages
          ? []
          : [...attachedImages, ...(options?.recordingImages ?? [])].filter(
              (img) => !img.uploading && img.imageId,
            );
      if (
        attachedImages.some(
          (attachment) => attachment.uploading || attachment.uploadError,
        )
      )
        return false;
      if (
        !options?.textOnly &&
        !options?.nativeChildInstruction &&
        submittedImages.some(attachmentIsImage) &&
        !modelSupportsVision(resolveModelForCapture())
      ) {
        useNotificationStore.getState().addSystemToast({
          id: "attachment-model-capability",
          title: translate(layoutMessages, "visionModelTitle"),
          level: "error",
          description: translate(layoutMessages, "visionModelBody"),
        });
        return false;
      }
      const originDisplayId =
        useCanvasStore.getState().cursorInput?.displayId ??
        useActiveDisplay.getState().activeDisplayId ??
        getWindowDisplayId();

      // Capture screenshots BEFORE the dispatch so their imageIds are
      // available in the same payload. Returns [] outside Tauri / when
      // capture is not requested / when the model does not support vision.
      // Capture at submit time so the screenshot reflects the app/window
      // the user is actually looking at when the request is sent.
      const requestedCapture = shouldCaptureCursorInputScreenshot(
        screenshotIntent,
        summonSelectionPresent,
      );
      const foregroundCapture = requestsForegroundScreenContext(text);
      const shouldCapture =
        !options?.textOnly &&
        !options?.skipAutoCapture &&
        (requestedCapture || foregroundCapture);
      const captured = await captureAndAttachIfEnabled(
        resolveModelForCapture(),
        shouldCapture,
        CURSOR_INPUT_SCREENSHOT_CAPTURE_SCOPE,
        foregroundCapture ? "precision" : undefined,
      );

      const readyImages = [...submittedImages, ...captured.images];
      // Allow image-only sends (no text) just like the global input bar.
      if (
        !text &&
        readyImages.length === 0 &&
        !options?.screenRecordings?.clips.length
      )
        return;

      const imageIds = readyImages.map((img) => img.imageId!);
      const prefixes = [captured.displaysToken, captured.pointerToken]
        .filter(Boolean)
        .join(" ");
      const finalText = prefixes ? `${prefixes} ${text}`.trim() : text;
      const projectId = data.currentProject?.id;
      const spaceId = data.currentSpace?.id;
      const startNewConversation =
        options?.startNewConversation ??
        inputBarRoutingIntent?.startNewConversation ??
        false;
      let dispatchResult;
      try {
        dispatchResult = dispatchUserMessage({
          text: finalText,
          literalText: options?.textOnly,
          nativeChildInstruction: options?.nativeChildInstruction,
          position: options?.position,
          agentKey: explicitAgentKey,
          execId: startNewConversation ? undefined : options?.executionId,
          runtimeSessionId: startNewConversation
            ? undefined
            : options?.runtimeSessionId,
          conversationId: startNewConversation
            ? undefined
            : options?.conversationId,
          imageIds,
          screenRecordings: options?.screenRecordings,
          maxParallelAgents,
          originDisplayId,
          conversationFrameId: options?.conversationFrameId,
          mentionedFrames: options?.mentionedFrames,
          composerId:
            options?.composerId ??
            (options?.conversationFrameId
              ? `conversation-frame:${options.conversationFrameId}`
              : destinationDraftComposerId(
                  inputDestinationKey({
                    startNewConversation,
                    conversationId:
                      options?.conversationId ??
                      inputBarRoutingIntent?.target?.conversationId,
                  }),
                )),
          startNewConversation,
          keepConversation: options?.keepConversation,
          ignoreInputBarSessionState: options?.ignoreInputBarSessionState,
          inputBarRoutingIntent,
        });
      } catch (error) {
        clearAttemptScreenshots(captured.images, handleRemoveImage);
        throw error;
      }
      if (!dispatchResult.sent) {
        clearAttemptScreenshots(captured.images, handleRemoveImage);
        return shouldCompleteInputSubmit(dispatchResult);
      }
      const previewRequestId = dispatchResult.clientRequestId;
      if (previewRequestId && captured.previews.length > 0) {
        beginCompactPromptScreenshotPreview(
          previewRequestId,
          captured.previews,
        );
      }
      if (!(await dispatchResult.accepted)) {
        if (previewRequestId) finishCompactResources(previewRequestId);
        clearAttemptScreenshots(captured.images, handleRemoveImage);
        return false;
      }

      const imageCount = readyImages.length;
      const optimisticDesc =
        text ||
        (imageCount > 0
          ? translate(layoutMessages, "sharedFiles", { count: imageCount })
          : translate(layoutMessages, "sentMessage"));
      // Activity is listed per project; with no project in view there is
      // no list to show it in.
      if (projectId) {
        pushActivity({
          id: `optimistic-${Date.now()}`,
          description: optimisticDesc,
          dotColor: "var(--color-status-selected)",
          createdAt: new Date().toISOString(),
          actorType: "user",
          projectId,
        });
      }

      const agentKey = dispatchResult.resolvedAgentKey;

      if (agentKey) {
        const resolvedAgent = data.inputAgents.find(
          (a) => a.name.toLowerCase() === agentKey,
        );
        if (resolvedAgent && resolvedAgent.id !== data.selectedAgentId) {
          data.handleSelectAgents(new Set([resolvedAgent.id]));
        }
      }

      // Drop the now-sent attachments from the shared strip. If the tray
      // canvas-copy option is enabled, also create image frames: in a row
      // from the prompt's position on the old canvas, placed by the backend
      // on the wall.
      if (
        projectId &&
        spaceId &&
        readyImages.length > 0 &&
        copyImagesToCanvas
      ) {
        import("@/api/spaces").then(({ createFrame }) => {
          const pastedImage = translate(layoutMessages, "pastedImage");
          const images = readyImages.filter(attachmentIsImage);
          const sizes = images.map(imageFrameSize);
          const positions = view.framePositions({
            kind: "near",
            near: options?.position,
            sizes,
          });
          images.forEach((img, index) => {
            const position = positions?.[index];
            createFrame(projectId, spaceId, {
              frame_type: "image",
              title: img.filename || pastedImage,
              content: {
                images: [
                  {
                    url: img.url,
                    alt: img.filename || pastedImage,
                    ...imagePixelSize(img),
                  },
                ],
              },
              ...(position
                ? { position_x: position.x, position_y: position.y }
                : {}),
              width: sizes[index]!.width,
              height: sizes[index]!.height,
            })
              .then((frame) => {
                ingestFrame(frame);
              })
              .catch((err: unknown) => {
                console.error(
                  "[image-frame] Failed to create image frame:",
                  err,
                );
              });
          });
        });
      }
      // Clear the strip — same shared state the global bar uses.
      readyImages.forEach((img) => handleRemoveImage(img.id));

      setScreenshotIntent(false);
      useOnboardingStore.getState().incrementMessageCount();
      return true;
    },
    [
      dispatchUserMessage,
      data,
      maxParallelAgents,
      pushActivity,
      attachedImages,
      handleRemoveImage,
      captureAndAttachIfEnabled,
      resolveModelForCapture,
      screenshotIntent,
      summonSelectionPresent,
      copyImagesToCanvas,
      setScreenshotIntent,
    ],
  );

  return handleCursorInputSubmit;
}
